// import { useState } from 'react';
import { capitalCase } from 'change-case';
import { useEffect, useState } from 'react';
// @mui
import { Container, Tab, Box, Tabs, Card, Typography, Avatar, Stack, alpha, useTheme, Fade, Grow } from '@mui/material';
// components
import { Page } from '../../../components';
import Iconify from '../../../components/iconify/Iconify';
import useTabs from '../../../hooks/useTabs';
import { AccountBilling, AccountChangePassword, AccountGeneral } from '../components';
import { getProfile } from '../../../shared/auth/authService';

// ----------------------------------------------------------------------

export default function UserAccount() {
  const theme = useTheme();
  // const [user, setUser] = useState({});
  const { user } = getProfile();
  const { currentTab, onChangeTab } = useTabs('profile');

  const ACCOUNT_TABS = [
    {
      value: 'profile',
      icon: <Iconify icon={'ic:round-account-box'} width={24} height={24} />,
      component: <AccountGeneral user={user} />,
      label: 'Profile',
      description: 'Manage your personal information',
    },
    {
      value: 'change_password',
      icon: <Iconify icon={'ic:round-vpn-key'} width={24} height={24} />,
      component: <AccountChangePassword />,
      label: 'Security',
      description: 'Update your password and security settings',
    },
  ];

  if (user.role === 'ADMIN') {
    const subscriptionMenu = {
      value: 'subscription',
      icon: <Iconify icon={'ic:round-receipt'} width={24} height={24} />,
      component: <AccountBilling />,
      label: 'Billing',
      description: 'Manage your subscription and billing',
    };
    if (!ACCOUNT_TABS.some((child) => child.value === subscriptionMenu.value)) {
      ACCOUNT_TABS.push(subscriptionMenu);
    }
  }

  return (
    <Page title="Account Settings">
      <Container maxWidth="xl">
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h3" sx={{ mb: 1, fontWeight: 700 }}>
            Account Settings
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your account preferences and settings
          </Typography>
        </Box>

        {/* User Profile Card */}
        <Card
          sx={{
            p: 3,
            mb: 4,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(
              theme.palette.secondary.main,
              0.08
            )} 100%)`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
            borderRadius: 2,
          }}
        >
          <Stack direction="row" spacing={3} alignItems="center">
            <Avatar
              src={user.photoURL}
              alt={user.display_name}
              sx={{
                width: 64,
                height: 64,
                border: `3px solid ${theme.palette.background.paper}`,
                boxShadow: theme.customShadows?.z8,
              }}
            >
              {user.display_name?.charAt(0)?.toUpperCase()}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                {user.display_name || 'User Name'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {user.email || 'user@example.com'}
              </Typography>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: theme.palette.primary.main,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {user.role || 'User'}
              </Box>
            </Box>
          </Stack>
        </Card>

        {/* Enhanced Tabs */}
        <Card sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            allowScrollButtonsMobile
            variant="scrollable"
            scrollButtons="auto"
            value={currentTab}
            onChange={onChangeTab}
            sx={{
              px: 2,
              bgcolor: theme.palette.background.neutral,
              '& .MuiTabs-indicator': {
                height: 3,
                borderRadius: '3px 3px 0 0',
                bgcolor: theme.palette.primary.main,
              },
              '& .MuiTab-root': {
                minHeight: 64,
                px: 3,
                py: 2,
                color: theme.palette.text.secondary,
                fontWeight: 600,
                fontSize: '0.875rem',
                textTransform: 'none',
                '&.Mui-selected': {
                  color: theme.palette.primary.main,
                },
                '&:hover': {
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                },
              },
            }}
          >
            {ACCOUNT_TABS.map((tab) => (
              <Tab
                disableRipple
                key={tab.value}
                label={
                  <Stack direction="row" spacing={1} alignItems="center">
                    {tab.icon}
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {tab.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.5 }}>
                        {tab.description}
                      </Typography>
                    </Box>
                  </Stack>
                }
                value={tab.value}
                sx={{ minWidth: 'auto', alignItems: 'flex-start' }}
              />
            ))}
          </Tabs>
        </Card>

        {/* Tab Content with Animation */}
        <Box sx={{ position: 'relative', minHeight: 400 }}>
          {ACCOUNT_TABS.map((tab) => {
            const isMatched = tab.value === currentTab;
            return (
              <Fade key={tab.value} in={isMatched} timeout={300}>
                <Box sx={{ display: isMatched ? 'block' : 'none' }}>
                  <Grow in={isMatched} timeout={400}>
                    <Box>{tab.component}</Box>
                  </Grow>
                </Box>
              </Fade>
            );
          })}
        </Box>
      </Container>
    </Page>
  );
}
